import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { cfpBaseUrl } from '../../network/urls';
import { officeLevelFromOfficeCode, resolveScopedTableOfficeCode } from '../../utils/adminCompany';

const initialState = {
    data: [],
    currentPage: 1,
    pageSize: 20,
    dateFrom: '',
    dateTo: '',
    loadFrom: '',
    loadTo: '',
    applicationProgressId: '',
    objectionStatus: '',
    totalPages: null,
    totalRecords: null,
    isLoading: false,
    error: null,
}

export const fetchPaginatedDisconnectionNormal = createAsyncThunk(
    'generalServices/fetchDisconnectionNormal',
    async ({ page, pageSize, dateFrom, dateTo, loadFrom, loadTo, applicationProgressId, objectionStatus, officeCode }, { getState }) => {
        const { auth } = getState();
        const { token, code, hierarchy, showAllApplicationsFlag, roleId, userId, selectedAdminCompanyCode } = auth;
        if (!token) {
            return { status: "400", data: [], totalPages: 1, totalRecords: 0 };
        }

        const myHeaders = new Headers();
        myHeaders.append("Authorization", token);
        myHeaders.append("Content-Type", "application/json");

        const effectiveOfficeCode = resolveScopedTableOfficeCode(code, roleId, officeCode, selectedAdminCompanyCode);
        const effectiveOfficeLevel = officeLevelFromOfficeCode(
            effectiveOfficeCode,
            code == 10 ? 2 : hierarchy,
        );
        const raw = JSON.stringify({
            pageNumber: String(page || 1),
            pageSize: String(pageSize || 20),
            officeCode: String(effectiveOfficeCode ?? ""),
            officeLevel: String(effectiveOfficeLevel ?? ""),
            showAllApplicationFlag: String(showAllApplicationsFlag ?? ""),
            overDueFlag: "0",
            dateFrom: dateFrom ? dateFrom : "",
            dateTo: dateTo ? dateTo : "",
            loadFrom: loadFrom !== undefined && loadFrom !== null && loadFrom !== "" ? String(loadFrom) : "",
            loadTo: loadTo !== undefined && loadTo !== null && loadTo !== "" ? String(loadTo) : "",
            applicationProgressId:
                applicationProgressId === 0 ||
                applicationProgressId === "0" ||
                (applicationProgressId !== undefined &&
                    applicationProgressId !== null &&
                    applicationProgressId !== "")
                    ? String(applicationProgressId)
                    : "",
            objectionStatus: objectionStatus ? String(objectionStatus) : "0",
            userId: roleId == 113 || roleId == 1146 ? String(userId ?? "") : "",
        });

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: raw,
            redirect: "follow"
        };

        const response = await fetch(cfpBaseUrl + `dco/getAllApplications`, requestOptions);
        let result;
        try {
            result = await response.json();
        } catch {
            throw new Error('Unable to fetch disconnection applications!');
        }

        const status = String(result?.status ?? response.status);
        if (status === "400") {
            return { status: "400", data: [], totalPages: 1, totalRecords: 0 };
        }
        if (status !== "200") {
            throw new Error(result?.message || 'Unable to fetch disconnection applications!');
        }

        return result;
    }
);

const disconnectionNormalPaginatedSlice = createSlice({
    name: 'disconnectionNormalPaginated',
    initialState: initialState,
    reducers: {
        nextPage(state) {
            state.currentPage += 1;
        },
        previousPage(state) {
            if (state.currentPage > 1) {
                state.currentPage -= 1;
            }
        },
        filterProgressId(state, action) {
            state.applicationProgressId = action.payload
        },
        resetProgressIdFilter(state) {
            state.applicationProgressId = ''
        },
        filterObjectionStatus(state, action) {
            state.objectionStatus = action.payload
        },
        resetObjectionStatusFilter(state) {
            state.applicationProgressId = ''
        },
        filterDateFrom(state, action) {
            state.dateFrom = action.payload
        },
        filterDateTo(state, action) {
            state.dateTo = action.payload
        },
        resetDateFilter(state) {
            state.dateFrom = ''
            state.dateTo = ''
        },
        filterLoadFrom(state, action) {
            state.loadFrom = action.payload
        },
        filterLoadTo(state, action) {
            state.loadTo = action.payload
        },
        resetLoadFilter(state) {
            state.loadFrom = ''
            state.loadTo = ''
        },
        firstPage(state) {
            state.currentPage = 1;
        },
        lastPage(state, action) {
            state.currentPage = action.payload;
        },
        resetState(state) {
            state.data = []
            state.currentPage = 1
            state.pageSize = 20
            state.dateFrom = ''
            state.dateTo = ''
            state.loadFrom = ''
            state.loadTo = ''
            state.applicationProgressId = ''
            state.objectionStatus = ''
            state.totalPages = null
            state.totalRecords = null
            state.isLoading = false
            state.error = null
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPaginatedDisconnectionNormal.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchPaginatedDisconnectionNormal.fulfilled, (state, action) => {
                state.isLoading = false;
                if (action.payload?.data) {
                    state.data = action.payload.data;
                } else {
                    state.data = [];
                }
                if (action.payload?.totalPages) {
                    state.totalPages = action.payload.totalPages;
                } else {
                    state.totalPages = 1;
                }
                state.totalRecords = action.payload?.totalRecords ?? 0;
                state.error = null;
            })
            .addCase(fetchPaginatedDisconnectionNormal.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.error.message;
                state.totalPages = null;
                state.totalRecords = null;
            });
    },
});

export const { nextPage, previousPage, firstPage, lastPage, filterProgressId, filterObjectionStatus, filterDateFrom, filterDateTo, resetProgressIdFilter, resetObjectionStatusFilter, resetDateFilter, resetState, filterLoadFrom, filterLoadTo, resetLoadFilter } = disconnectionNormalPaginatedSlice.actions;

export default disconnectionNormalPaginatedSlice.reducer;
